import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { COMPANY } from "@/data/content";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why" },
  { label: "Service Area", href: "#area" },
  { label: "Commercial", to: "/commercial-roofing" },
  { label: "Insurance Claims", to: "/insurance-roof-replacement" },
  { label: "Recent Projects", to: "/recent-projects" },
  { label: "Blog", to: "/blog" },
  { label: "Reviews", href: "#reviews" },
  { label: "FAQ", href: "#faq" },
];

export default function Header({ onQuote }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img src="/honeybee-logo-light.svg" alt="Honey Bee Roofing & Exteriors logo" className="h-14 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-x-6 gap-y-1 flex-wrap justify-end max-w-2xl">
          {LINKS.map((l) =>
            l.to ? (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-zinc-300 hover:text-honey transition-colors tracking-wide whitespace-nowrap"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-zinc-300 hover:text-honey transition-colors tracking-wide whitespace-nowrap"
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            data-testid="header-call-link"
            className="flex items-center gap-2 text-sm font-semibold text-white hover:text-honey transition-colors"
          >
            <Phone className="w-4 h-4 text-honey" />
            {COMPANY.phone}
          </a>
          <button
            onClick={onQuote}
            data-testid="header-quote-btn"
            className="bg-honey text-white font-bold text-sm px-5 py-2.5 rounded-sm hover:bg-honey-600 transition-colors"
          >
            Free Evaluation
          </button>
        </div>

        <button
          className="lg:hidden text-white"
          data-testid="mobile-menu-btn"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-5 py-6 flex flex-col gap-4"
          data-testid="mobile-menu"
        >
          {LINKS.map((l) =>
            l.to ? (
              <Link key={l.label} to={l.to} onClick={() => setOpen(false)}
                className="text-base font-medium text-zinc-200 py-1">{l.label}</Link>
            ) : (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)}
                className="text-base font-medium text-zinc-200 py-1">{l.label}</a>
            )
          )}
          <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-2 text-base font-semibold text-honey py-1">
            <Phone className="w-4 h-4" /> {COMPANY.phone}
          </a>
          <button
            onClick={() => { setOpen(false); onQuote(); }}
            className="bg-honey text-white font-bold px-5 py-3 rounded-sm mt-2"
          >
            Get Free Evaluation
          </button>
        </motion.div>
      )}
    </motion.header>
  );
}
